import React from 'react';
import styled from 'styled-components';
import {
  screenXsMin,
  screenSmMin,
  screenMdMin,
  screenLgMin,
  Text,
  Type,
} from '@spotify-internal/encore-web';
import {
  cssLayoutMargin,
  cssSpacing,
} from '@spotify-internal/encore-web/core/styles';

import LegalTerms from './LegalTerms';
import Container from './Container';

interface SectionWrapperPropTypes
  extends React.ComponentProps<typeof Container> {
  background: {
    color: string;
    size?: 'cover' | 'contain' | 'auto';
    wideDesktop: string;
    desktop: string;
    tablet: string;
    mobile: string;
    mobileMargin: string;
    tabletMargin: string;
  };
  offset?: number;
  isFullHeight?: boolean;
}

export const SectionWrapper = styled(Container)<SectionWrapperPropTypes>`
  ${({ color }) => color && `--text-base: ${color}`};
  /*
   * fallback ensures retrocompatibility on old implementations
   * of this component, without a theme defined
   */
  color: var(--text-base, '#FFF');

  a {
    color: var(--text-base, '#FFF');
    text-decoration: underline;
  }

  ${({ isFullHeight }) =>
    isFullHeight ? 'min-height: 100vh;' : 'min-height: auto;'}

  @media (min-width: ${screenSmMin}) {
    min-height: 70vh;
  }

  > article {
    box-sizing: border-box;
    height: auto;
    display: flex;
    flex-direction: column;
    margin: 0 auto;

    @media (min-width: 768px) {
      width: 750px;
    }

    @media (min-width: 992px) {
      width: 970px;
    }

    @media (min-width: 1200px) {
      width: 1170px;
    }
  }

  ${({ textAlign }) => textAlign && `text-align: ${textAlign}`};

  /*
   * this selector is a dirty trick to increase specificity.
   * another "background" rule, overriding "background-color"
   * may be specified below - we aim to support both background colors
   * AND setting multiple background properties for images.
   */
  && {
    ${({ background }) =>
      background &&
      background.color &&
      `background-color: ${background.color}`};
  }
  ${({ background }) =>
    background && background.size && `background-size: ${background.size}`};
  ${({ background }) =>
    background &&
    background.mobileMargin &&
    `padding-bottom: ${background.mobileMargin};`}
  ${({ background }) =>
    background && background.mobile && `background: ${background.mobile}`};

  @media (min-width: ${screenXsMin}) {
    ${({ background }) =>
      background &&
      background.tabletMargin &&
      `padding-bottom: ${background.tabletMargin};`};
    ${({ background }) =>
      background && background.tablet && `background: ${background.tablet};`};
  }

  @media (min-width: ${screenMdMin}) {
    ${({ background }) =>
      background && background.mobileMargin && `padding-bottom: 0;`};
    ${({ background }) =>
      background && background.desktop && `background: ${background.desktop};`};
  }

  @media (min-width: ${screenLgMin}) {
    ${({ background }) =>
      background &&
      background.wideDesktop &&
      `background: ${background.wideDesktop};`};
    ${({ offset = 0 }) =>
      offset && `margin-top: ${offset}px; padding-top: ${-offset}px;`};
  }
`;

export const MainHeader = styled.header`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

export const Content = styled.div`
  padding: 35px 0;
  max-width: 940px;
  align-self: flex-start;
  -webkit-font-smoothing: antialiased;
`;

type HeaderPropTypes = {
  fontWeightValue?: 'bold' | 'regular';
  fontSizeValue?:
    | 'large'
    | 'medium-large'
    | 'medium-small'
    | 'medium'
    | 'small';
  width?: number;
  isSub?: boolean;
  addSpacing?: boolean;
};

/**
 * The font-size uses the formula in following article https://levelup.gitconnected.com/css-responsive-fluid-typography-a23a9aab19a
 * in order to responsively resize on small screens:
 * 24 = minimum size we have according to the font size for @media (min-width: ${screenXsMin}) small
 * 80 = maximum size we have according to the font size for @media (min-width: ${screenSmMin}) large
 * 375 = is the width for iPhone 6/7/8 -> smallest size we want to support but still scalable and readable for sizes below like iPhone 5's
 * 1200 = maximum width I used the standard large screen size we support at Spotify (not including XL for TV's)
 */

export const Header = styled(Type.h1)<HeaderPropTypes>`
  line-height: ${({ addSpacing }) => (addSpacing ? '1.5' : '1.2')};
  margin: 0;
  padding: 0;
  font-weight: 900;
  max-width: 100%;
  width: auto;
  display: inline-block;

  font-size: ${({ fontSizeValue }) => {
    switch (fontSizeValue) {
      default:
      case 'large':
        return `calc(24px + (48 - 24) * ((100vw - 320px) / (480 - 320)))`;
      case 'medium-large':
        return `calc(24px + (32 - 24) * ((100vw - 320px) / (480 - 320)))`;
      case 'medium':
        return `calc(24px + (28 - 24) * ((100vw - 320px) / (480 - 320)))`;
      case 'small':
        return `calc(24px + (24 - 24) * ((100vw - 320px) / (480 - 320)))`;
    }
  }};

  @media (min-width: ${screenXsMin}) {
    font-size: ${({ fontSizeValue }) => {
      switch (fontSizeValue) {
        default:
        case 'large':
          return '48px';
        case 'medium-large':
          return '32px';
        case 'medium':
          return '28px';
        case 'small':
          return '24px';
      }
    }};
  }

  @media (min-width: ${screenSmMin}) {
    font-size: ${({ fontSizeValue }) => {
      switch (fontSizeValue) {
        default:
        case 'large':
          return '80px';
        case 'medium-large':
          return '60px';
        case 'medium-small':
          return '48px';
        case 'medium':
          return '40px';
        case 'small':
          return '24px';
      }
    }};
  }

  @media (min-width: ${screenMdMin}) {
    width: ${({ width = 'auto' }) => width};
  }
`;

export const SecondaryHeader = styled(Type.h2)<HeaderPropTypes>`
  line-height: 1.5;
  margin: 15px 0;
  padding: 0;
  ${({ isSub }) => (isSub ? 'margin-bottom: 0;' : '')}
  width: 100%;
  max-width: 100%;
  display: inline-block;

  ${({ fontWeightValue }) => {
    switch (fontWeightValue) {
      default:
      case 'bold':
        return 'font-weight: 600;';
      case 'regular':
        return 'font-weight: 400;';
    }
  }}

  font-size: ${({ fontSizeValue }) => {
    switch (fontSizeValue) {
      default:
      case 'large':
        return '24px';
      case 'medium':
        return '18px';
      case 'small':
        return '18px';
    }
  }};

  @media (min-width: ${screenXsMin}) {
    width: ${({ width = 'auto' }) => width};
    margin: 30px 0;
  }

  @media (min-width: 768px) {
    font-size: ${({ fontSizeValue }) => {
      switch (fontSizeValue) {
        default:
        case 'large':
          return '30px';
        case 'medium':
          return '24px';
        case 'small':
          return '16px';
      }
    }};

    ${({ isSub }) => (isSub ? 'margin-bottom: 0;' : '')}
  }
`;

export const LegalContainer = styled.footer`
  font-size: 12px;
  margin-top: ${cssSpacing('looser-4')};
  width: 100%;
`;

export const LegalText = styled(LegalTerms)`
  display: inline-block;
  @media (min-width: ${screenMdMin}) {
    max-width: 630px;
  }
`;

export const ButtonGroup = styled.div`
  display: block;
  margin: 16px 0;
  > div {
    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }
  a {
    width: auto;
    box-sizing: border-box;
    text-decoration: none;
  }
  @media (min-width: 768px) {
    margin: 32px 0 0;
    > div {
      &:not(:last-child) {
        margin-right: 8px;
        margin-bottom: 0;
      }
      display: inline-block;
    }
  }
`;

export type VerticalAlignmentType = {
  mobile: 'center' | 'bottom';
  desktop: 'center' | 'top' | 'bottom' | 'none';
};

export const SharingContainer = styled.div<{ textAlign?: string }>`
  max-width: 400px;
  ${({ textAlign }) =>
    textAlign === 'center'
      ? `margin: ${cssLayoutMargin('base')} auto auto auto`
      : `margin-top: ${cssLayoutMargin('base')}`};

  @media (min-width: ${screenLgMin}) {
    margin: ${cssLayoutMargin('base')} 0 auto 0;
  }
`;

export const ButtonWrapper = styled.footer<{ hasImages: boolean }>`
  text-align: center;
  margin-top: 30px;
  ${({ hasImages }) =>
    hasImages &&
    `
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    justify-content: center;
    > div:first-of-type {
      margin-right: 18px;
    }
  `}
  @media (min-width: ${screenSmMin}) {
    margin: 50px auto 0 auto;
  }
`;

export const StyledHeader = styled(Header)`
  > h2 {
    max-width: 670px;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const ButtonGroupText = styled.p`
  width: 100%;
`;

export const Legal = styled(LegalTerms)`
  margin: 10px 0 0 0;
  font-size: 12px;
  text-align: center;
  a {
    color: inherit;
    text-decoration: underline;
  }
  @media (min-width: ${screenSmMin}) {
    font-size: 14px;
  }
`;

export const HeaderText = styled(Text)`
  text-wrap: wrap;
  margin: 0 0 ${cssSpacing('tighter-2')};
`;
